import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Gem, Sparkles, Share2, Clock } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import { useProfile } from "@/lib/useProfile";
import { AppShell } from "@/components/app-shell";
import { ImageBox, prefetchPhotoUrl, useResolvedPhotoUrl } from "@/components/ui-extras";
import { type MoodBoardItem } from "@/components/item-detail-sections";
import { LookOfDaySection } from "@/components/look-of-day-section";
import { CaileveOrbitBoard } from "@/components/caileve-orbit-board";
import { WelcomeCard } from "@/components/welcome-card";
import { Sentry } from "@/lib/sentry";
import { LandingPage } from "@/components/landing-page";
import { TierArrival } from "@/components/tier-arrival";
import { PhotoUpload } from "@/components/photo-upload";
import { useSubscription } from "@/lib/useSubscription";
import { useShareCaileve } from "@/hooks/use-share-caileve";
import {
  ContextualPrompt,
  WeeklyPrompt,
  CollectionDepthIndicator,
  ConsideringPrompt,
  useTotalCollectionCount,
} from "@/components/collection-prompts";
import React, { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { useMilestones } from "@/lib/milestones";
import { MilestonePromptCard } from "@/components/milestone-prompt-card";
import { evaluateFoundationCoverage } from "@/lib/style-engine/daily-look-selection";

const STOCK_STYLE_ME = "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=800&q=80&fit=crop";
const STOCK_CREATE_LOOK = "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80&fit=crop";

const STOCK_EYE = "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=800&q=80&fit=crop";

const STOCK_WARDROBE_FIT = STOCK_EYE;
const STOCK_LOOK_FIT = "https://images.unsplash.com/photo-1509631179647-0177331693ae?w=800&q=80&fit=crop";
const STOCK_LOOKBOOK = "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=800&q=80&fit=crop";

function LookFitIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="var(--caileve-gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v3" />
      <path d="M9 5h6" />
      <path d="M9 5l-3 3v2l2-1 1 3-1 9h8l-1-9 1-3 2 1v-2l-3-3" />
    </svg>
  );
}

function rotatingSlice<T>(pool: T[], displayCount: number): T[] {
  if (pool.length <= displayCount) return pool;
  const dayIndex = Math.floor(Date.now() / 86400000);
  const start = dayIndex % pool.length;
  const result: T[] = [];
  for (let i = 0; i < displayCount; i++) {
    result.push(pool[(start + i) % pool.length]);
  }
  return result;
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Cailève — Style the pieces you already love" },
      {
        name: "description",
        content:
          "Your personal luxury styling concierge. Curate jewelry, build looks, and receive editorial styling advice.",
      },
      { name: "p:domain_verify", content: "5532f533279da0d2e8e3e7110710b0eb" },
    ],
  }),
  component: () => (
    <IndexRoute />
  ),
});

function IndexRoute() {
  const { user, loading } = useAuth();
  const [arrivalDone, setArrivalDone] = useState(false);
  if (loading) {
    return <div className="fixed inset-0" style={{ backgroundColor: "var(--caileve-bg)" }} />;
  }
  if (!user) {
    return <LandingPage />;
  }
  return (
    <>
      {!arrivalDone && <TierArrival onDone={() => setArrivalDone(true)} />}
      <AppShell>
        <Home />
      </AppShell>
    </>
  );
}


function Home() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const { tier } = useSubscription();
  const { data: totalCount } = useTotalCollectionCount();

  // Category-aware pre-generation gate. A raw row count (>=2) let a
  // closet like "one bottom + one necklace" fire generation and produce
  // a lopsided look. We instead require a jewelry piece AND a clothing
  // foundation (one_piece, or base_top + bottom).
  const { data: foundation } = useQuery({
    queryKey: ["daily_look_foundation", uid],
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      const [clothingRes, jewelryRes] = await Promise.all([
        supabase
          .from("clothing_items")
          .select("id, name, clothing_type, style_attributes")
          .eq("user_id", uid)
          .eq("is_wishlist", false)
          .eq("is_archived", false)
          .limit(200),
        supabase
          .from("jewelry_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid)
          .eq("is_wishlist", false)
          .eq("is_archived", false)
          .eq("is_accessory", false),
      ]);
      const clothingItems = (clothingRes.data ?? []) as Array<{
        id: string; name: string; clothing_type: string | null; style_attributes: unknown;
      }>;
      const jewelryCount = jewelryRes.count ?? 0;
      return evaluateFoundationCoverage({ clothingItems, jewelryCount });
    },
  });
  const canGenerateDailyLook = foundation?.canGenerate ?? false;
  const foundationMissingMessage = foundation?.missingMessage ?? null;

  type HomeFeedFavCard = { kind: "jewelry" | "clothing" | "accessory"; id: string; name: string; photo_url: string | null; caption: string | null; sort_at?: string | null };
  type HomeFeedRecentCard = { kind: "jewelry" | "clothing" | "accessory"; id: string; name: string; photo_url: string | null; caption: string | null; created_at: string };
  type HomeFeedRediscoverCard = { kind: "jewelry" | "clothing" | "accessory"; id: string; name: string; photo_url: string | null; category: string | null };
  type HomeFeed = { favorites: HomeFeedFavCard[]; recent_items: HomeFeedRecentCard[]; rediscover_items: HomeFeedRediscoverCard[] };

  const todayKey = new Date().toISOString().slice(0, 10);
  const { data: dailyData, isLoading: dailyDataLoading } = useQuery({
    queryKey: ["daily_insight", uid, todayKey],
    enabled: !!uid,
    staleTime: 20 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      const { data } = await supabase.functions.invoke("caileve-daily-insight", { body: {} });
      const result = data as { greeting?: string; home_feed?: HomeFeed };
      return {
        greeting: result?.greeting ?? null,
        home_feed: result?.home_feed ?? { favorites: [], recent_items: [], rediscover_items: [] },
      };
    },
  });

  const { data: dailyLookData, isLoading: dailyLookLoading } = useQuery({
    queryKey: ["daily_look", uid, todayKey],
    enabled: !!uid && canGenerateDailyLook,
    staleTime: 20 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      // Attempt geolocation for weather-aware daily look.
      // Non-blocking — falls back gracefully if unavailable or denied.
      let lat: number | null = null;
      let lon: number | null = null;
      try {
        const pos = await new Promise<GeolocationPosition | null>((resolve) => {
          if (typeof navigator === "undefined" || !navigator.geolocation) {
            resolve(null);
            return;
          }
          let settled = false;
          const done = (p: GeolocationPosition | null) => {
            if (settled) return;
            settled = true;
            resolve(p);
          };
          navigator.geolocation.getCurrentPosition(
            (p) => done(p),
            () => done(null),
            { enableHighAccuracy: false, timeout: 4000, maximumAge: 60_000 * 10 }
          );
          setTimeout(() => done(null), 4500);
        });
        if (pos) {
          lat = pos.coords.latitude;
          lon = pos.coords.longitude;
        }
      } catch {
        // Geolocation unavailable — proceed without weather
      }
      const { data } = await supabase.functions.invoke("caileve-daily-look", {
        body: lat !== null && lon !== null ? { lat, lon } : {},
      });
      const result = data as { daily_look_request_id?: string; reason?: string };
      if (!result?.daily_look_request_id && result?.reason) {
        console.error(`[caileve-daily-look] No look generated: ${result.reason}`);
      }
      return {
        daily_look_request_id: result?.daily_look_request_id ?? null,
      };
    },
  });
  const insight = dailyData?.greeting ?? null;
  const dailyLookRequestId = dailyLookData?.daily_look_request_id ?? null;

  // Track ContextualPrompt dismissal via a lifted callback so only one of
  // the two prompts is ever visible at a time. Initial value mirrors the
  // sessionStorage flag set by a prior dismissal in this tab.
  const [contextualDismissed, setContextualDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      sessionStorage.getItem("caileve_contextual_prompt_dismissed") === "1",
  );

  const [insightDismissed, setInsightDismissed] = useState(() => {
    const key = `caileve_insight_dismissed_${new Date().toISOString().slice(0, 10)}`;
    return localStorage.getItem(key) === "1";
  });

  const [continueNudgeDismissed, setContinueNudgeDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem("caileve_continue_nudge_dismissed") === "1",
  );

  const { data: profile, isLoading: profileLoading, isFetched: profileFetched } = useProfile();

  const { data: clothingCount } = useQuery({
    queryKey: ["clothing_count", uid],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { count } = await supabase
        .from("clothing_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid);
      return count ?? 0;
    },
  });

  const recentLoading = dailyDataLoading;
  const recentItems = rotatingSlice(dailyData?.home_feed?.recent_items ?? [], 2);

  useEffect(() => {
    if (!recentItems) return;
    recentItems.slice(0, 2).forEach((item) => prefetchPhotoUrl(item.photo_url));
  }, [recentItems]);

  const favorites: HomeFeedFavCard[] = dailyData?.home_feed?.favorites ?? [];

  const displayedFavorites = rotatingSlice(favorites, 2);

  useEffect(() => {
    if (!displayedFavorites) return;
    displayedFavorites.slice(0, 2).forEach((item) => prefetchPhotoUrl(item.photo_url));
  }, [displayedFavorites]);

  const queryClient = useQueryClient();

  const [cardPhotoOpen, setCardPhotoOpen] = useState(false);
  const [wardrobeFitPhotoOpen, setWardrobeFitPhotoOpen] = useState(false);
  const [lookFitPhotoOpen, setLookFitPhotoOpen] = useState(false);
  const [lookbookPhotoOpen, setLookbookPhotoOpen] = useState(false);

  const [cardPhotoPath, setCardPhotoPath] = useState<string | null>(
    () =>
      ((profile?.onboarding_milestones as Record<string, unknown> | null | undefined)
        ?.home_card_photo as string | null | undefined) ?? null,
  );
  const [wardrobeFitPhotoPath, setWardrobeFitPhotoPath] = useState<string | null>(
    () =>
      ((profile?.onboarding_milestones as Record<string, unknown> | null | undefined)
        ?.home_card_photo_eye as string | null | undefined) ?? null,
  );
  const [lookFitPhotoPath, setLookFitPhotoPath] = useState<string | null>(
    () =>
      ((profile?.onboarding_milestones as Record<string, unknown> | null | undefined)
        ?.home_card_photo_look_fit as string | null | undefined) ?? null,
  );
  const [lookbookPhotoPath, setLookbookPhotoPath] = useState<string | null>(
    () =>
      ((profile?.onboarding_milestones as Record<string, unknown> | null | undefined)
        ?.home_card_photo_lookbook as string | null | undefined) ?? null,
  );

  const saveCardPhoto = async (path: string | null) => {
    setCardPhotoPath(path);
    const existing =
      (profile?.onboarding_milestones as Record<string, unknown> | null | undefined) ?? {};
    await supabase
      .from("profiles")
      .update({ onboarding_milestones: { ...existing, home_card_photo: path } })
      .eq("id", uid);
    queryClient.invalidateQueries({ queryKey: ["profile", uid] });
  };

  const saveWardrobeFitPhoto = async (path: string | null) => {
    setWardrobeFitPhotoPath(path);
    const existing =
      (profile?.onboarding_milestones as Record<string, unknown> | null | undefined) ?? {};
    await supabase
      .from("profiles")
      .update({ onboarding_milestones: { ...existing, home_card_photo_eye: path } })
      .eq("id", uid);
    queryClient.invalidateQueries({ queryKey: ["profile", uid] });
  };

  const saveLookFitPhoto = async (path: string | null) => {
    setLookFitPhotoPath(path);
    const existing =
      (profile?.onboarding_milestones as Record<string, unknown> | null | undefined) ?? {};
    await supabase
      .from("profiles")
      .update({ onboarding_milestones: { ...existing, home_card_photo_look_fit: path } })
      .eq("id", uid);
    queryClient.invalidateQueries({ queryKey: ["profile", uid] });
  };

  const saveLookbookPhoto = async (path: string | null) => {
    setLookbookPhotoPath(path);
    const existing =
      (profile?.onboarding_milestones as Record<string, unknown> | null | undefined) ?? {};
    await supabase
      .from("profiles")
      .update({ onboarding_milestones: { ...existing, home_card_photo_lookbook: path } })
      .eq("id", uid);
    queryClient.invalidateQueries({ queryKey: ["profile", uid] });
  };

  useEffect(() => {
    const m = (profile?.onboarding_milestones as Record<string, unknown> | null | undefined) ?? {};
    const styleMe = m.home_card_photo as string | null | undefined;
    const wardrobeFit = m.home_card_photo_eye as string | null | undefined;
    const lookFit = m.home_card_photo_look_fit as string | null | undefined;
    const lookbook = m.home_card_photo_lookbook as string | null | undefined;
    if (styleMe !== undefined) setCardPhotoPath(styleMe ?? null);
    if (wardrobeFit !== undefined) setWardrobeFitPhotoPath(wardrobeFit ?? null);
    if (lookFit !== undefined) setLookFitPhotoPath(lookFit ?? null);
    if (lookbook !== undefined) setLookbookPhotoPath(lookbook ?? null);
  }, [profile]);

  useEffect(() => {
    if (!uid) return;
    const todayKey = new Date().toISOString().slice(0, 10);
    const flagKey = `caileve_backfill_attempted_${todayKey}`;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(flagKey) === "1") return;
    localStorage.setItem(flagKey, "1");
    supabase.functions.invoke("caileve-backfill-attributes", { body: {} }).catch(() => {
      // Silent by design — no UI, no toast, no retry this session.
    });
  }, [uid]);

  // Only show a name once the current user's profile has fully resolved.
  // Never fall back to the email prefix — that briefly showed a stale/wrong
  // identifier between sessions.
  const profileResolved = !profileLoading && profileFetched;
  const firstName =
    profileResolved && profile?.id === uid && profile?.name
      ? profile.name.split(" ")[0]
      : "";

  const hour = new Date().getHours();
  const isMorning = hour >= 5 && hour < 12;
  const isAfternoon = hour >= 12 && hour < 18;
  const greeting = isMorning ? "Good Morning" : isAfternoon ? "Good Afternoon" : "Good Evening";
  const tagline = "Your collection, thoughtfully styled.";

  const share = useShareCaileve();

  const rediscoverItems: HomeFeedRediscoverCard[] = dailyData?.home_feed?.rediscover_items ?? [];

  useEffect(() => {
    if (!rediscoverItems) return;
    rediscoverItems.slice(0, 2).forEach((item) => prefetchPhotoUrl(item.photo_url));
  }, [rediscoverItems]);

  const { data: lookOfDay, isLoading: lookOfDayLoading } = useQuery({
    queryKey: ["look_of_day", dailyLookRequestId],
    enabled: !!uid && !!dailyLookRequestId,
    staleTime: 20 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      if (!dailyLookRequestId) return null;
      const { data } = await supabase
        .from("style_requests")
        .select(
          "id, event, season, time_of_day, suggested_look, jewelry_item_id, clothing_item_id, accessory_item_id, selected_item_ids, weather_context, missing_slots",
        )
        .eq("id", dailyLookRequestId)
        .eq("user_id", uid!)
        .maybeSingle();
      return data ?? null;
    },
  });

  const { data: styleRequestsCount } = useQuery({
    queryKey: ["style_requests_count", uid],
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      const { count } = await supabase
        .from("style_requests")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid!);
      return count ?? 0;
    },
  });

  const selectedItemRefs = (lookOfDay?.selected_item_ids as Array<{ id: string; type: string }> | null) ?? [];
  const jewelryRefIds = selectedItemRefs.filter((s) => s.type === "jewelry" || s.type === "accessory").map((s) => s.id);
  const clothingRefIds = selectedItemRefs.filter((s) => s.type === "clothing").map((s) => s.id);

  const { data: lodJewelryRows } = useQuery({
    queryKey: ["lod_jewelry_rows", jewelryRefIds.join(",")],
    enabled: jewelryRefIds.length > 0,
    staleTime: 20 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      const { data } = await supabase
        .from("jewelry_items")
        .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color")
        .in("id", jewelryRefIds);
      return data ?? [];
    },
  });

  const { data: lodClothingRows } = useQuery({
    queryKey: ["lod_clothing_rows", clothingRefIds.join(",")],
    enabled: clothingRefIds.length > 0,
    staleTime: 20 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      const { data } = await supabase
        .from("clothing_items")
        .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color")
        .in("id", clothingRefIds);
      return data ?? [];
    },
  });

  const lodJewelryLoading = jewelryRefIds.length === 0 ? false : lodJewelryRows === undefined;
  const lodClothingLoading = clothingRefIds.length === 0 ? false : lodClothingRows === undefined;

  const dailyLookIsLoading =
    totalCount === undefined ||
    dailyLookLoading ||
    (!!dailyLookRequestId && lookOfDayLoading) ||
    (!!lookOfDay && (lodJewelryLoading || lodClothingLoading));

  const eyeVerdictCount = (profile?.eye_verdict_count as number | null | undefined) ?? 0;

  const activeMilestone: { milestoneKey: string; copy: string; linkTo?: string; linkLabel?: string } | null = (() => {
    // Milestones are evaluated in priority order. First uncompleted one wins.
    // The milestones object comes from useMilestones — do not re-read it here.
    // Instead pass the milestoneKey to MilestonePromptCard and let it self-gate.
    // This function only determines WHICH milestone is active based on data state.
    if ((totalCount ?? 0) === 0) return null; // nothing to prompt yet

    // Priority 1 — first piece added (fires after first item exists, before Style Me has been run)
    if ((totalCount ?? 0) >= 1 && (styleRequestsCount ?? 0) === 0 && eyeVerdictCount === 0) {
      return {
        milestoneKey: "onboarding_first_piece",
        copy: "Your collection has begun. Try Style Me to see your first look.",
        linkTo: "/suggest",
        linkLabel: "Begin Styling",
      };
    }

    // Priority 2a — first Eye scan completed, no Style Me run yet — direct to wardrobe
    if (eyeVerdictCount >= 1 && (styleRequestsCount ?? 0) === 0) {
      return {
        milestoneKey: "onboarding_first_eye_scan",
        copy: "That is The Whole Trip beginning. The more you scan, the sharper Cailève's memory becomes.",
      };
    }

    // Priority 2b — first Eye scan completed AND Style Me has been run — congratulate and seed Daily Look
    if (eyeVerdictCount >= 1 && (styleRequestsCount ?? 0) >= 1 && (totalCount ?? 0) < 10) {
      return {
        milestoneKey: "onboarding_eye_and_style_me",
        copy: "Cailève has seen your first scan and built your first look. Open it tomorrow morning — it will be different.",
        linkTo: "/",
        linkLabel: "See today's look",
      };
    }


    // Priority 3 — first Style Me look built
    if ((styleRequestsCount ?? 0) >= 1 && (totalCount ?? 0) < 10) {
      return {
        milestoneKey: "onboarding_first_style_me",
        copy: "Your first look is saved. Cailève will remember the pieces it chose — and reach for the ones you haven't worn in a while next time.",
      };
    }

    // Priority 4 — collection reaches 10 pieces
    if ((totalCount ?? 0) >= 10) {
      return {
        milestoneKey: "onboarding_ten_pieces",
        copy: "Cailève's intelligence is growing with your collection. Daily Look is sharpest from here.",
      };
    }

    return null;
  })();

  const hasYourEdit = (recentItems?.length ?? 0) > 0;
  const hasRediscover = (totalCount ?? 0) >= 8 && (rediscoverItems?.length ?? 0) > 0;
  const hasMostReachedFor = favorites.length > 0;
  const halfDayIndex = Math.floor(Date.now() / 43200000);
  const cyclePosition = halfDayIndex % 5;

  const conciergeChoice: "your_edit" | "rediscover" | "most_reached_for" | null = recentLoading
    ? "your_edit"
    : (() => {
        const sequence = [
          "your_edit",
          "most_reached_for",
          "your_edit",
          "rediscover",
          "your_edit",
        ] as const;
        const preferred = sequence[cyclePosition];

        if (preferred === "your_edit" && hasYourEdit) return "your_edit";
        if (preferred === "most_reached_for" && hasMostReachedFor) return "most_reached_for";
        if (preferred === "rediscover" && hasRediscover) return "rediscover";

        if (hasYourEdit) return "your_edit";
        if (hasMostReachedFor) return "most_reached_for";
        if (hasRediscover) return "rediscover";
        return null;
      })();

  const CARD_KEYS = ["wardrobe_fit", "look_fit", "lookbook"] as const;
  type CardKey = (typeof CARD_KEYS)[number];
  const cardDefs: Record<CardKey, {
    to: string;
    search?: Record<string, unknown>;
    eyebrow: string;
    title: string;
    caption: string;
    stockPhoto: string;
    cardPhotoPath: string | null;
    icon: ReactNode;
    onLongPress: () => void;
  }> = {
    wardrobe_fit: {
      to: "/caileve-eye",
      search: { mode: "wardrobe" },
      eyebrow: "Wardrobe Fit",
      title: "Does this belong?",
      caption: "Photograph any piece. Know before you buy.",
      stockPhoto: STOCK_WARDROBE_FIT,
      cardPhotoPath: wardrobeFitPhotoPath,
      icon: <Gem size={28} strokeWidth={1.5} style={{ color: "var(--caileve-muted)" }} />,
      onLongPress: () => setWardrobeFitPhotoOpen(true),
    },
    look_fit: {
      to: "/caileve-eye",
      search: { mode: "look" },
      eyebrow: "Final Touch",
      title: "Is it the right piece?",
      caption: "Anchor what you're wearing. Check the last addition.",
      stockPhoto: STOCK_LOOK_FIT,
      cardPhotoPath: lookFitPhotoPath,
      icon: <LookFitIcon size={28} />,
      onLongPress: () => setLookFitPhotoOpen(true),
    },
    lookbook: {
      to: "/scan-history",
      eyebrow: "Eye History",
      title: "Everything you considered.",
      caption: "Every piece you scanned, and what Cailève said.",
      stockPhoto: STOCK_LOOKBOOK,
      cardPhotoPath: lookbookPhotoPath,
      icon: <Clock size={28} strokeWidth={1.5} style={{ color: "var(--caileve-muted)" }} />,
      onLongPress: () => setLookbookPhotoOpen(true),
    },
  };
  const savedOrder = profile?.home_layout?.order;
  const cardOrder: CardKey[] =
    savedOrder && savedOrder.length === 3 && CARD_KEYS.every((k) => savedOrder.includes(k))
      ? (savedOrder as CardKey[])
      : [...CARD_KEYS];
  const hiddenCards = new Set(profile?.home_layout?.hidden ?? []);
  const visibleCardOrder = cardOrder.filter((k) => !hiddenCards.has(k));
  const cardPairSlice = visibleCardOrder.slice(0, 2);
  const cardRestSlice = visibleCardOrder.slice(2);

  return (
    <div>
      <WelcomeCard />

      {/* Greeting — compact, app-native vertical padding */}
      <section className="max-w-5xl mx-auto px-8 pt-6 pb-4">
        <h1
          className="font-serif-display text-3xl md:text-4xl leading-[1.1] text-[var(--caileve-ink)]"
        >
          {firstName
            ? `${greeting}, ${firstName}.`
            : profileResolved
              ? "What are we styling today?"
              : `${greeting}.`}
        </h1>
        <p
          className="font-serif-display italic text-lg mt-2"
          style={{ color: "var(--caileve-lilac)" }}
        >
          {tagline}
        </p>
      </section>

      {insight && !insightDismissed && (
        <section className="max-w-5xl mx-auto px-8 pb-4">
          <div
            className="px-5 py-4"
            style={{
              borderLeft: "2px solid var(--caileve-gold)",
              backgroundColor: "rgba(198, 166, 107, 0.06)",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <p
              className="italic text-base leading-relaxed flex-1"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "var(--caileve-ink)",
              }}
            >
              {insight}
            </p>
            <button
              type="button"
              aria-label="Dismiss insight"
              onClick={() => {
                const key = `caileve_insight_dismissed_${new Date().toISOString().slice(0, 10)}`;
                localStorage.setItem(key, "1");
                setInsightDismissed(true);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--caileve-muted)",
                padding: 0,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </section>
      )}

      {(totalCount === undefined || dailyLookIsLoading || (lookOfDay && (jewelryRefIds.length === 0 || lodJewelryRows) && (clothingRefIds.length === 0 || lodClothingRows))) && (
        <div className="mt-6">
          <LookOfDaySection
            request={lookOfDay ?? null}
            selectedItemRefs={selectedItemRefs}
            jewelryRows={lodJewelryRows ?? []}
            clothingRows={lodClothingRows ?? []}
            isLoading={dailyLookIsLoading}
          />
        </div>
      )}

      {/* Category-aware empty state: user has items but not a complete
          foundation (jewelry + one_piece OR base_top + bottom). Tells
          them what to add rather than firing a lopsided generation. */}
      {!dailyLookIsLoading && !canGenerateDailyLook && (totalCount ?? 0) > 0 && foundationMissingMessage && (
        <section className="max-w-5xl mx-auto px-8 pt-2 pb-6 mt-6">
          <p
            className="text-[10px] uppercase tracking-[0.2em]"
            style={{ color: "var(--caileve-lilac)" }}
          >
            ✦ Today's Cailève Look
          </p>
          <p
            className="mt-3 italic text-base leading-relaxed"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: "var(--caileve-ink)",
            }}
          >
            {foundationMissingMessage}
          </p>
        </section>
      )}


      {/* Action cards — hero Style Me + two secondary side-by-side */}
      <section className="max-w-7xl mx-auto px-8 pb-6 mt-8 flex flex-col gap-3">
        <StyleMeCard cardPhotoPath={cardPhotoPath} onLongPress={() => setCardPhotoOpen(true)} />

        {cardPairSlice.length === 2 && (
          <div className="flex gap-3" style={{ alignItems: "stretch" }}>
            {cardPairSlice.map((key) => {
              const d = cardDefs[key];
              return (
                <div key={key} className="flex-1 min-w-0" style={{ display: "flex", flexDirection: "column" }}>
                  <ActionPhotoCard
                    to={d.to}
                    search={d.search}
                    eyebrow={d.eyebrow}
                    title={d.title}
                    caption={d.caption}
                    stockPhoto={d.stockPhoto}
                    cardPhotoPath={d.cardPhotoPath}
                    icon={d.icon}
                    onLongPress={d.onLongPress}
                  />
                </div>
              );
            })}
          </div>
        )}
        {cardPairSlice.length === 1 && (() => {
          const d = cardDefs[cardPairSlice[0]];
          return (
            <ActionPhotoCard
              to={d.to}
              search={d.search}
              eyebrow={d.eyebrow}
              title={d.title}
              caption={d.caption}
              stockPhoto={d.stockPhoto}
              cardPhotoPath={d.cardPhotoPath}
              icon={d.icon}
              onLongPress={d.onLongPress}
            />
          );
        })()}
        {cardRestSlice.map((key) => {
          const d = cardDefs[key];
          return (
            <ActionPhotoCard
              key={key}
              to={d.to}
              search={d.search}
              eyebrow={d.eyebrow}
              title={d.title}
              caption={d.caption}
              stockPhoto={d.stockPhoto}
              cardPhotoPath={d.cardPhotoPath}
              icon={d.icon}
              onLongPress={d.onLongPress}
            />
          );
        })}
        <button
          type="button"
          onClick={() => share()}
          className="mt-1 text-[11px] uppercase self-center flex items-center gap-1.5"
          style={{
            letterSpacing: "0.25em",
            color: "var(--caileve-muted)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px 4px",
          }}
        >
          <Share2 size={13} strokeWidth={1.5} />
          Share a look, or share Cailève
        </button>
      </section>

      {/* Intelligent prompts */}
      <div className="max-w-7xl mx-auto px-8 mb-2">
        {activeMilestone && (
          <MilestonePromptCard
            milestoneKey={activeMilestone.milestoneKey}
            copy={activeMilestone.copy}
            linkTo={activeMilestone.linkTo}
            linkLabel={activeMilestone.linkLabel}
          />
        )}
        <AmbientHintStack
          hints={[
            {
              hintKey: "hint_clothing_1",
              show: (clothingCount ?? 0) >= 1,
              message: "Tap any piece to add it to your look as you browse.",
            },
            {
              hintKey: "hint_style_me_intro",
              show: (totalCount ?? 0) >= 1,
              message: "Ready to see what Cailève can do? Try Style Me.",
              to: "/suggest",
            },
          ]}
        />
        <CollectionDepthIndicator />
        {/* Only one nudge message at a time, in priority order */}
        {(() => {
          // Priority 1: collection too small for full intelligence (3–9 items)
          if ((totalCount ?? 0) >= 3 && (totalCount ?? 0) < 10) {
            const msg = (clothingCount ?? 0) === 0
              ? "Add a clothing piece and Cailève can build your first complete look around your jewelry."
              : (totalCount ?? 0) - (clothingCount ?? 0) === 0
                ? "Add a jewelry piece — it sets the tone for every look Cailève builds."
                : (totalCount ?? 0) < 5
                  ? "Your palette is forming. A few more pieces and Cailève will begin to know you."
                  : "Cailève is learning your style. Every piece you add sharpens the intelligence.";
            return (
              <div className="px-5 py-4 mb-3" style={{ borderLeft: "2px solid var(--caileve-gold)", backgroundColor: "rgba(198, 166, 107, 0.06)" }}>
                <p className="italic text-base leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}>{msg}</p>
              </div>
            );
          }
          // Priority 2: approaching intelligence threshold (10–14 items)
          if ((totalCount ?? 0) >= 10 && (totalCount ?? 0) < 15) {
            return (
              <div className="px-5 py-4 mb-3" style={{ borderLeft: "2px solid var(--caileve-gold)", backgroundColor: "rgba(198, 166, 107, 0.06)" }}>
                <p className="italic text-base leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}>Cailève is beginning to understand your occasions and your signature pieces.</p>
              </div>
            );
          }
          // Priority 3: full intelligence — one rotating contextual prompt
          if ((totalCount ?? 0) >= 15) {
            return (
              <>
                <ConsideringPrompt />
                {!contextualDismissed
                  ? <ContextualPrompt onDismissed={() => setContextualDismissed(true)} />
                  : <WeeklyPrompt />}
              </>
            );
          }
          // Priority 4: continue nudge (1–9 items, dismissable) — only if nothing above matched
          if ((totalCount ?? 0) >= 1 && !continueNudgeDismissed) {
            return (
              <div className="px-5 py-3 mb-3 flex items-center gap-3" style={{ borderLeft: "2px solid var(--caileve-lilac)", backgroundColor: "rgba(155, 127, 168, 0.06)" }}>
                <p className="italic text-sm leading-relaxed flex-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}>
                  {(clothingCount ?? 0) === 0
                    ? "Add a clothing piece and Cailève can build complete looks."
                    : (totalCount ?? 0) - (clothingCount ?? 0) === 0
                      ? "Add a jewelry piece — it sets the tone for every look."
                      : "Add another piece — Cailève learns more with every item."}
                </p>
                <button type="button" aria-label="Dismiss"
                  onClick={() => { localStorage.setItem("caileve_continue_nudge_dismissed", "1"); setContinueNudgeDismissed(true); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--caileve-muted)", fontSize: 16, padding: "0 4px", flexShrink: 0 }}>
                  ×
                </button>
              </div>
            );
          }
          return null;
        })()}
      </div>

      <div className="mt-8">
        {conciergeChoice === "your_edit" && (
          <YourEditSection items={recentItems} loading={recentLoading} />
        )}
        {conciergeChoice === "rediscover" && (
          <RediscoverSection items={rotatingSlice(rediscoverItems ?? [], 2)} />
        )}
        {conciergeChoice === "most_reached_for" && (
          <MostReachedForSection items={displayedFavorites} />
        )}
      </div>

      <HomeCardPhotoSheet
        open={cardPhotoOpen}
        onOpenChange={setCardPhotoOpen}
        userId={uid}
        folder="home"
        currentPath={cardPhotoPath}
        label="Style Me background"
        onSave={saveCardPhoto}
      />
      <HomeCardPhotoSheet
        open={wardrobeFitPhotoOpen}
        onOpenChange={setWardrobeFitPhotoOpen}
        userId={uid}
        folder="home"
        currentPath={wardrobeFitPhotoPath}
        label="Wardrobe Fit background"
        onSave={saveWardrobeFitPhoto}
      />
      <HomeCardPhotoSheet
        open={lookFitPhotoOpen}
        onOpenChange={setLookFitPhotoOpen}
        userId={uid}
        folder="home"
        currentPath={lookFitPhotoPath}
        label="Final Touch background"
        onSave={saveLookFitPhoto}
      />
      <HomeCardPhotoSheet
        open={lookbookPhotoOpen}
        onOpenChange={setLookbookPhotoOpen}
        userId={uid}
        folder="home"
        currentPath={lookbookPhotoPath}
        label="Eye History background"
        onSave={saveLookbookPhoto}
      />

    </div>
  );
}

type AmbientHintSpec = {
  hintKey: string;
  message: string;
  show: boolean;
  to?: string;
};

function AmbientHintStack({ hints }: { hints: AmbientHintSpec[] }) {
  const { milestones, setMilestone, milestonesLoading } = useMilestones();
  if (milestonesLoading) return null;
  const active = hints.find(
    (h) => h.show && milestones[h.hintKey] !== true,
  );
  if (!active) return null;
  const { hintKey, message, to } = active;
  const text = (
    <p
      className="italic text-left flex-1"
      style={{ fontSize: 13, color: "var(--caileve-muted)", fontFamily: "'Cormorant Garamond', Georgia, serif" }}
    >
      {message}
    </p>
  );
  return (
    <div className="flex items-center justify-between gap-3 mb-4">
      {to ? (
        <Link to={to} className="flex-1 hover:opacity-80">
          {text}
        </Link>
      ) : (
        text
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setMilestone(hintKey, true)}
        className="flex-shrink-0 hover:opacity-70"
        style={{ color: "var(--caileve-muted)" }}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function YourEditSection({
  items,
  loading,
}: {
  items:
    | Array<{
        kind: "jewelry" | "clothing" | "accessory";
        id: string;
        name: string;
        photo_url: string | null;
        caption: string | null;
      }>
    | undefined;
  loading: boolean;
}) {
  if (!loading && (!items || items.length === 0)) {
    return (
      <section className="max-w-7xl mx-auto px-8 pb-8">
        <YourEditHeader />
        <p
          className="italic text-base py-2"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-muted)" }}
        >
          Nothing in Your Edit yet. Scan a piece with Cailève Eye to start.
        </p>
      </section>
    );
  }
  const placeholders = [0, 1, 2, 3];
  const cards = loading ? null : items!;

  const cardInner = (
    item: {
      kind: "jewelry" | "clothing" | "accessory";
      id: string;
      name: string;
      photo_url: string | null;
      caption: string | null;
    },
  ) => (
    <>
      <div className="w-full overflow-hidden" style={{ height: 150 }}>
        <ImageBox src={item.photo_url} alt={item.name} aspect="h-full w-full" />
      </div>
      <p
        className="font-serif-display text-base mt-2 leading-tight truncate"
        style={{ color: "var(--caileve-ink)" }}
      >
        {item.name}
      </p>
      <p
        className="uppercase mt-1 pb-3 truncate"
        style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}
      >
        {item.caption ?? item.kind}
      </p>
    </>
  );

  const linkTo = (kind: "jewelry" | "clothing" | "accessory") =>
    kind === "jewelry" ? "/jewelry/$id" : kind === "clothing" ? "/clothing/$id" : "/accessories/$id";

  return (
    <section className="max-w-7xl mx-auto px-8 pb-8">
      <YourEditHeader />

      {/* Mobile: horizontal scroll */}
      <div
        className="md:hidden flex gap-4 hide-scrollbar"
        style={{
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollbarWidth: "none",
        }}
      >
        {loading
          ? placeholders.map((i) => (
              <div key={i} style={{ width: 150, flexShrink: 0, scrollSnapAlign: "start" }}>
                <div
                  className="animate-pulse"
                  style={{ width: 150, height: 150, backgroundColor: "var(--caileve-soft)" }}
                />
                <div
                  className="animate-pulse mt-2"
                  style={{ height: 14, width: "80%", backgroundColor: "var(--caileve-soft)" }}
                />
                <div
                  className="animate-pulse mt-2"
                  style={{ height: 10, width: "50%", backgroundColor: "var(--caileve-soft)" }}
                />
              </div>
            ))
          : cards!.slice(0, 2).map((item) => (
              <Link
                key={`${item.kind}-${item.id}`}
                to={linkTo(item.kind)}
                params={{ id: item.id }}
                style={{ width: 160, flexShrink: 0, scrollSnapAlign: "start" }}
                className="block"
              >
                {cardInner(item)}
              </Link>
            ))}
      </div>

      {/* Desktop: 4-col grid */}
      <div className="hidden md:grid grid-cols-4 gap-5">
        {loading
          ? placeholders.map((i) => (
              <div key={i}>
                <div
                  className="animate-pulse w-full"
                  style={{ aspectRatio: "1 / 1", backgroundColor: "var(--caileve-soft)" }}
                />
                <div
                  className="animate-pulse mt-3"
                  style={{ height: 16, width: "75%", backgroundColor: "var(--caileve-soft)" }}
                />
                <div
                  className="animate-pulse mt-2"
                  style={{ height: 10, width: "45%", backgroundColor: "var(--caileve-soft)" }}
                />
              </div>
            ))
          : cards!.map((item) => (
              <Link
                key={`${item.kind}-${item.id}`}
                to={linkTo(item.kind)}
                params={{ id: item.id }}
                className="block group"
              >
                <ImageBox src={item.photo_url} alt={item.name} />
                <p
                  className="font-serif-display text-lg mt-3 leading-tight"
                  style={{ color: "var(--caileve-ink)" }}
                >
                  {item.name}
                </p>
                <p
                  className="uppercase mt-1 pb-3"
                  style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}
                >
                  {item.caption ?? item.kind}
                </p>
              </Link>
            ))}
      </div>
    </section>
  );
}

function YourEditHeader() {
  return (
    <Link to="/your-edit" style={{ display: "block", textDecoration: "none", color: "inherit" }}>
      <div className="flex items-baseline justify-between mb-6 border-b border-[var(--caileve-copper)]/25 pb-4">
        <div>
          <h2 className="font-serif-display text-2xl" style={{ color: "var(--caileve-lilac)" }}>
            <span style={{ color: "var(--caileve-gold-text)", marginRight: 8 }}>✦</span>
            Your Edit
          </h2>
          <p
            className="italic text-base mt-1"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-muted)" }}
          >
            Pieces waiting for your decision.
          </p>
        </div>
      </div>
    </Link>
  );
}

function RediscoverSection({
  items,
}: {
  items: Array<{ kind: "jewelry" | "clothing" | "accessory"; id: string; name: string; photo_url: string | null; category: string | null }> | undefined;
}) {
  if (!items || items.length === 0) return null;
  const linkTo = (kind: "jewelry" | "clothing" | "accessory") =>
    kind === "jewelry" ? "/jewelry/$id" : kind === "clothing" ? "/clothing/$id" : "/accessories/$id";
  return (
    <section className="max-w-7xl mx-auto px-8 pb-8">
      <Link to="/rediscover" style={{ display: "block", textDecoration: "none", color: "inherit" }}>
        <div className="flex items-baseline justify-between mb-6 border-b border-[var(--caileve-copper)]/25 pb-4">
          <div>
            <h2 className="font-serif-display text-2xl" style={{ color: "var(--caileve-lilac)" }}>
              <span style={{ color: "var(--caileve-gold-text)", marginRight: 8 }}>✦</span>
              REDISCOVER
            </h2>
            <p
              className="italic text-base mt-1"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-muted)" }}
            >
              Pieces you haven't worn lately.
            </p>
          </div>
        </div>
      </Link>
      <div
        className="md:hidden flex gap-4 hide-scrollbar"
        style={{ overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none" }}
      >
        {items.slice(0, 2).map((item) => (
          <Link
            key={`${item.kind}-${item.id}`}
            to={linkTo(item.kind)}
            params={{ id: item.id }}
            className="block"
            style={{ width: 150, flexShrink: 0, scrollSnapAlign: "start" }}
          >
            <div className="w-full overflow-hidden" style={{ height: 150 }}>
              <ImageBox src={item.photo_url} alt={item.name} aspect="h-full w-full" />
            </div>
            <p
              className="font-serif-display text-base mt-2 leading-tight truncate"
              style={{ color: "var(--caileve-ink)" }}
            >
              {item.name}
            </p>
            {item.category && (
              <p
                className="uppercase mt-1 pb-3 truncate"
                style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}
              >
                {item.category}
              </p>
            )}
          </Link>
        ))}
      </div>
      <div className="hidden md:grid md:grid-cols-4 gap-4">
        {items.map((item) => (
          <Link
            key={`${item.kind}-${item.id}`}
            to={linkTo(item.kind)}
            params={{ id: item.id }}
            className="block"
          >
            <div className="w-full overflow-hidden" style={{ height: 150 }}>
              <ImageBox src={item.photo_url} alt={item.name} aspect="h-full w-full" />
            </div>
            <p
              className="font-serif-display text-base mt-2 leading-tight truncate"
              style={{ color: "var(--caileve-ink)" }}
            >
              {item.name}
            </p>
            {item.category && (
              <p
                className="uppercase mt-1 pb-3 truncate"
                style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}
              >
                {item.category}
              </p>
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}

function MostReachedForSection({
  items,
}: {
  items: Array<{ kind: "jewelry" | "clothing" | "accessory"; id: string; name: string; photo_url: string | null; caption: string | null }>;
}) {
  return (
    <section className="max-w-7xl mx-auto px-8 pb-8">
      <Link
        to="/favorites"
        style={{ display: "block", textDecoration: "none", color: "inherit" }}
      >
        <div className="flex items-baseline justify-between mb-6 border-b border-[var(--caileve-copper)]/25 pb-4">
          <div>
            <h2 className="font-serif-display text-2xl text-[var(--caileve-lilac)]"><span style={{ color: "var(--caileve-gold-text)", marginRight: 8 }}>✦</span>Most Reached For</h2>
            <p
              className="italic text-base mt-1"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "var(--caileve-muted)",
              }}
            >
              The pieces you return to again and again.
            </p>
          </div>
        </div>
      </Link>
      {items.length > 0 ? (
        <>
          <div
            className="md:hidden flex gap-5 hide-scrollbar"
            style={{
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none",
            }}
          >
            {items.slice(0, 2).map((item) => {
              const route =
                item.kind === "jewelry"
                  ? "/jewelry/$id"
                  : item.kind === "clothing"
                    ? "/clothing/$id"
                    : "/accessories/$id";
              return (
                <Link
                  key={`${item.kind}-${item.id}`}
                  to={route}
                  params={{ id: item.id }}
                  className="group block"
                  style={{ width: "calc(42vw - 16px)", flexShrink: 0, scrollSnapAlign: "start" }}
                >
                  <ImageBox src={item.photo_url} alt={item.name} />
                  <p className="font-serif-display text-lg mt-3 leading-tight text-[var(--caileve-ink)]">
                    {item.name}
                  </p>
                  <p className="text-[10px] uppercase mt-1 pb-3" style={{ letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
                    {item.caption ?? item.kind}
                  </p>
                </Link>
              );
            })}
          </div>
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-6 gap-5">
            {items.map((item) => {
              const route =
                item.kind === "jewelry"
                  ? "/jewelry/$id"
                  : item.kind === "clothing"
                    ? "/clothing/$id"
                    : "/accessories/$id";
              return (
                <Link
                  key={`${item.kind}-${item.id}`}
                  to={route}
                  params={{ id: item.id }}
                  className="group block"
                >
                  <ImageBox src={item.photo_url} alt={item.name} />
                  <p className="font-serif-display text-lg mt-3 leading-tight text-[var(--caileve-ink)]">
                    {item.name}
                  </p>
                  <p className="text-[10px] uppercase mt-1 pb-3" style={{ letterSpacing: "0.25em", color: "var(--caileve-muted)" }}>
                    {item.caption ?? item.kind}
                  </p>
                </Link>
              );
            })}
          </div>
        </>
      ) : (
        <p
          className="italic text-lg py-8"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-muted)" }}
        >
          Mark a piece as a favorite from your collection to feature it here.
        </p>
      )}
    </section>
  );
}

function useLongPress(callback: () => void, ms = 650) {
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = React.useRef(false);

  const start = React.useCallback(
    (_e: React.TouchEvent) => {
      firedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        callback();
      }, ms);
    },
    [callback, ms],
  );

  const cancel = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  };
}

function HomeCardPhotoSheet({
  open,
  onOpenChange,
  userId,
  currentPath,
  label,
  onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: string;
  folder: "home";
  currentPath: string | null;
  label: string;
  onSave: (path: string | null) => Promise<void>;
}) {
  const [pending, setPending] = useState<string | null>(currentPath);
  useEffect(() => {
    if (open) setPending(currentPath);
  }, [open, currentPath]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-0 pb-10"
        style={{ backgroundColor: "var(--caileve-bg)" }}
      >
        <div className="mx-auto mt-2 mb-4 h-1 w-10 rounded-full bg-[var(--caileve-muted)]/40" />
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl text-center text-[var(--caileve-ink)]">
            {label}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-6">
          <PhotoUpload
            userId={userId}
            folder="home"
            value={pending}
            onChange={(path) => setPending(path)}
          />
        </div>
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex-1 py-3 text-sm uppercase tracking-[0.2em]"
            style={{
              border: "1px solid var(--caileve-soft)",
              color: "var(--caileve-muted)",
              backgroundColor: "transparent",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              await onSave(pending);
              onOpenChange(false);
            }}
            className="flex-1 py-3 text-sm uppercase tracking-[0.2em]"
            style={{
              backgroundColor: "var(--caileve-lilac)",
              color: "var(--caileve-bg)",
              border: "none",
            }}
          >
            Save
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StyleMeCard({
  cardPhotoPath,
  onLongPress,
}: {
  cardPhotoPath: string | null;
  onLongPress: () => void;
}) {
  const longPress = useLongPress(onLongPress);
  const { url: cardPhotoUrl } = useResolvedPhotoUrl(cardPhotoPath);
  const bgSrc = cardPhotoUrl || STOCK_STYLE_ME;
  return (
    <div
      style={{
        position: "relative",
        touchAction: "pan-y",
        WebkitTouchCallout: "none",
        userSelect: "none",
      }}
      {...longPress}
    >
      <Link
        to="/suggest"
        className="group block w-full transition-opacity hover:opacity-90 active:opacity-75"
        style={{ position: "relative", overflow: "hidden", backgroundColor: "var(--caileve-lilac)", padding: "28px 32px", minHeight: 220, display: "block" }}
      >
        <img src={bgSrc} alt="" aria-hidden="true"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }} />
        <div aria-hidden="true"
          style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(to right, rgba(30,22,36,0.82) 0%, rgba(30,22,36,0.48) 55%, rgba(30,22,36,0.12) 100%)", zIndex: 1 }} />
        <div style={{ position: "relative", zIndex: 2 }}>
          <p
            className="text-[10px] uppercase tracking-[0.25em]"
            style={{ color: "rgba(248,245,250,0.85)" }}
          >
            Editorial Concierge
          </p>
          <p
            className="font-serif-display text-3xl mt-2"
            style={{ color: "var(--caileve-bg)" }}
          >
            Style Me
          </p>
          <p
            className="font-serif-display italic text-base mt-1"
            style={{ color: "rgba(248,245,250,0.85)" }}
          >
            Style the pieces you already love.
          </p>
        </div>
      </Link>
    </div>
  );
}

function ActionPhotoCard({ to, search, onClick, eyebrow, title, caption, stockPhoto, cardPhotoPath, icon, onLongPress }: {
  to?: string;
  search?: Record<string, unknown>;
  onClick?: () => void | Promise<void>;
  eyebrow: string;
  title: string;
  caption: string;
  stockPhoto: string;
  cardPhotoPath: string | null;
  icon?: ReactNode;
  onLongPress?: () => void;
}) {
  const longPress = useLongPress(onLongPress ?? (() => {}));
  const { url: cardPhotoUrl } = useResolvedPhotoUrl(cardPhotoPath);
  const bgSrc = cardPhotoUrl || stockPhoto;
  const innerStyle: React.CSSProperties = { position: "relative", overflow: "hidden", backgroundColor: "var(--caileve-soft)", padding: "28px 32px", minHeight: 160, height: "100%", display: "block", width: "100%", textAlign: "left", border: 0, cursor: "pointer" };
  const inner = (
    <>
      <img src={bgSrc} alt="" aria-hidden="true"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }} />
      <div aria-hidden="true"
        style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(to bottom, rgba(248,245,250,0.94) 0%, rgba(237,228,240,0.65) 50%, rgba(237,228,240,0.08) 100%)", zIndex: 1 }} />
      <div style={{ position: "relative", zIndex: 2 }}>
        {icon && <div style={{ marginBottom: 8 }}>{icon}</div>}
        <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--caileve-muted)" }}>{eyebrow}</p>
        <p className="font-serif-display text-3xl mt-2" style={{ color: "var(--caileve-ink)" }}>{title}</p>
        <p className="font-serif-display italic text-base mt-1" style={{ color: "var(--caileve-muted)" }}>{caption}</p>
      </div>
    </>
  );
  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        touchAction: "pan-y",
        WebkitTouchCallout: "none",
        userSelect: "none",
      }}
      {...longPress}
    >
      {to ? (
        <Link
          to={to}
          search={search as never}
          className="group block w-full transition-opacity hover:opacity-90 active:opacity-75"
          style={innerStyle}
        >
          {inner}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => onClick?.()}
          className="group block transition-opacity hover:opacity-90 active:opacity-75"
          style={innerStyle}
        >
          {inner}
        </button>
      )}
    </div>
  );
}


